import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, ArrowRight } from 'lucide-react';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.password.trim()) newErrors.password = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const result = await login(formData);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setErrors(prev => ({ ...prev, submit: result.error || 'Invalid email or password. Please try again.' }));
      }
    } catch (_error) {
      setErrors(prev => ({ ...prev, submit: 'Invalid email or password. Please try again.' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* LEFT SIDE - FORM */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8">
            <Link to="/">
              <img 
                src="/2026_logo.png" 
                alt="Student Signal" 
                className="h-[48px] w-auto"
                style={{ imageRendering: '-webkit-optimize-contrast' }}
              />
            </Link>
          </div>

          {/* Welcome Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-semibold mb-2" style={{ color: '#1A1A1A' }}>Welcome Back</h1>
            <p style={{ color: '#6B7280' }}>Sign in to continue your college journey</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-10 pr-4 py-3 text-sm transition-all"
                  style={{
                    border: errors.email ? '1px solid #EF4444' : '1px solid #E2E5E7',
                    borderRadius: '6px',
                    color: '#1A1A1A',
                    backgroundColor: '#FFFFFF'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#10614E'}
                  onBlur={(e) => e.target.style.borderColor = errors.email ? '#EF4444' : '#E2E5E7'}
                />
              </div>
              {errors.email && (
                <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-10 pr-4 py-3 text-sm transition-all"
                  style={{
                    border: errors.password ? '1px solid #EF4444' : '1px solid #E2E5E7',
                    borderRadius: '6px',
                    color: '#1A1A1A',
                    backgroundColor: '#FFFFFF'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#10614E'}
                  onBlur={(e) => e.target.style.borderColor = errors.password ? '#EF4444' : '#E2E5E7'}
                />
              </div>
              {errors.password && (
                <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{errors.password}</p>
              )}
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-3 text-sm" style={{ backgroundColor: '#FEF2F2', color: '#EF4444', borderRadius: '6px' }}>
                {errors.submit}
              </div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 text-sm font-semibold text-white transition-all flex items-center justify-center gap-2"
              style={{
                backgroundColor: loading ? '#6B7280' : '#10614E',
                borderRadius: '6px',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                if (!loading) e.target.style.backgroundColor = '#0A4638';
              }}
              onMouseLeave={(e) => {
                if (!loading) e.target.style.backgroundColor = '#10614E';
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
              {!loading && <ArrowRight size={18} />}
            </button>
          </form>

          {/* Links */}
          <div className="mt-6 text-center">
            <p className="text-sm" style={{ color: '#6B7280' }}>
              Don&apos;t have an account?{' '}
              <Link to="/signup" className="font-medium" style={{ color: '#10614E' }}>
                Create account
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE - VISUAL BRAND PANEL */}
      <div 
        className="hidden lg:flex lg:w-1/2 items-center justify-center p-12"
        style={{
          backgroundColor: '#10614E',
          backgroundImage: 'linear-gradient(135deg, #10614E 0%, #0A4638 100%)'
        }}
      >
        <div className="max-w-lg">
          {/* Modular Promo Card */}
          <div 
            className="p-8"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
            }}
          >
            <div className="mb-4">
              <span 
                className="text-xs font-semibold px-3 py-1 rounded-full"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: '#FFFFFF'
                }}
              >
                FEATURED
              </span>
            </div>
            
            <h2 className="text-3xl font-bold text-white mb-2">
              Featured College of the Week
            </h2>
            
            <p className="text-xl text-white/90 mb-6">
              Arizona State University
            </p>
            
            <p className="text-white/80 mb-6 leading-relaxed">
              Discover innovative programs, world-class faculty, and exceptional opportunities at one of America&apos;s most innovative universities.
            </p>
            
            <button
              className="px-6 py-3 font-semibold text-sm transition-all"
              style={{
                backgroundColor: '#FFFFFF',
                color: '#10614E',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              }}
            >
              Explore Programs
            </button>
          </div>

          {/* Bottom Stats */}
          <div className="mt-8 grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">2,500+</div>
              <div className="text-xs text-white/70">Colleges</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">$50M+</div>
              <div className="text-xs text-white/70">In Scholarships</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">100K+</div>
              <div className="text-xs text-white/70">Students</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
